import { memo, useCallback } from 'react';

import { useAppSelector } from '@/store/hooks';
import useLoadCategories from '@/connection/hooks/use-load-categories';
import { getCategories, getIsLoadingCategories } from '@/store/slices/categories/selectors';
import { TReactSelectOption } from '@ui/select/react-select/custom-react-select.types';
import EditStateSelect from '@ui/edit-state-wrapper/edit-state-select';
import preventMultiValueArrowNav from '@ui/edit-state-wrapper/prevent-multi-value-arrow-nav';

type CategoriesSelectProps = {
  defaultCategories?: TReactSelectOption[];
  onMenuClose: (selectedCategories: TReactSelectOption[]) => void;
  disabled?: boolean;
  dynamicMenuPlacement?: boolean;
  dynamicMenuPlacementOffset?: number;
};

const CategoriesSelect = memo(
  ({
    disabled,
    defaultCategories,
    onMenuClose,
    dynamicMenuPlacement,
    dynamicMenuPlacementOffset = 0,
  }: CategoriesSelectProps) => {
    useLoadCategories({ skipIfCategoriesExist: true });

    const loadingCategories = useAppSelector(getIsLoadingCategories);
    const channelCategories = useAppSelector(getCategories);

    const handleMenuClose = useCallback(
      (selectedCategories: TReactSelectOption[]) => {
        onMenuClose(selectedCategories);
      },
      [onMenuClose]
    );

    return (
      <EditStateSelect
        dynamicMenuPlacement={dynamicMenuPlacement}
        dynamicMenuPlacementOffset={dynamicMenuPlacementOffset}
        Label="Categories"
        placeholder={!disabled ? 'Add categories' : ''}
        defaultValue={defaultCategories}
        disabled={disabled}
        isLoading={loadingCategories}
        options={(channelCategories || [])?.map((c) => ({
          label: c.toString(),
          value: c.toString(),
          meta: { label: c.toString(), value: c.toString() },
        }))}
        onMenuClose={handleMenuClose}
        onKeyDown={preventMultiValueArrowNav}
      />
    );
  }
);

export default CategoriesSelect;
